import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowRight,
  CalendarDays,
  Check,
  CircleDollarSign,
  Disc3,
  Flag,
  PauseCircle,
  Download,
  FileSpreadsheet,
  Link2,
  ListMusic,
  Loader2,
  Search,
  SlidersHorizontal,
  Tag,
  Trash2,
  Upload,
  Users,
  X,
} from "lucide-react";
import Layout from "@/components/Layout";
import StickyFilters from "@/components/StickyFilters";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import DateRangeFilter, { EMPTY_RANGE, rangeActive, rangeParams } from "@/components/DateRangeFilter";
import { Chips, FilterSelect, Pill } from "@/components/Music";
import { EmptyState, LoadingPanel, StatCard } from "@/components/Bits";
import { Bars, Money, SOURCE_KIND, Threshold, TrackPicker, money, monthLabel, monthRange, serviceName, shownCode, termsFor } from "@/components/Royalty";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useConfirm } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, downloadFile, formatDate, formatDateTime } from "@/lib/api";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useRemembered } from "@/lib/useRemembered";
import { MyRoyalties } from "@/pages/Writer";
import { useTenant } from "@/context/TenantContext";
import { cn } from "@/lib/utils";

const DATE_FIELDS = [
  { value: "usage", label: "Usage month" },
  { value: "statement", label: "Statement date" },
];
const BMI_DATE_FIELDS = [
  { value: "usage", label: "Quarter performed" },
  { value: "statement", label: "Report date" },
];
const EMPTY = { ...EMPTY_RANGE, field: "usage" };

const TABS = [
  ["works", "Works"],
  ["lines", "Lines"],
  ["writers", "Writers"],
  ["statements", "Statements"],
];

const WORK_CHIPS = [
  ["all", "All"],
  ["current", "Current plays"],
  ["backpay", "Has back-pay"],
  ["negative", "Went negative"],
  ["unmatched", "Not linked to a track"],
  ["disputed", "Disputed"],
];
const BMI_CHIPS = [
  ["all", "All"],
  ["unmatched", "Not linked to a track"],
];
const LINE_CHIPS = [
  ["all", "All types"],
  ["current", "Current plays"],
  ["backpay", "Back-pay"],
  ["negative", "Went negative"],
];

/** Gold tag + the disputed recording(s) on a work, for the Works list and cards. */
function NotOursTag() {
  return <Pill className="ml-2 align-middle">Not ours — resolved</Pill>;
}
function DisputeTag() {
  return (
    <Pill tone="warn" className="ml-2 align-middle">
      Disputed
    </Pill>
  );
}
function DisputeLine({ w }) {
  if (!w.disputes?.length) return null;
  return (
    <div className="text-[11.5px] text-gold truncate">
      {w.disputes.map((d) => `${d.rec_title || d.isrc}${d.artist ? ` — ${d.artist}` : ""}`).join(", ")} · <Money value={w.held} className="text-[11.5px]" /> held
    </div>
  );
}

// The same rules as the server's chips, so switching a chip filters the list already on screen
// at once instead of waiting for a new one.
const WORK_FILTERS = {
  all: () => true,
  current: (w) => w.current_lines > 0,
  backpay: (w) => w.backpay_lines > 0,
  negative: (w) => w.amount < -1e-12,
  unmatched: (w) => w.match === "none" || w.match === "title",
  disputed: (w) => !!w.disputes?.length,
};
const LINE_FILTERS = {
  all: () => true,
  current: (l) => !l.backpay,
  backpay: (l) => !!l.backpay,
  negative: (l) => l.amount < -1e-12,
};
const sumOf = (rows, k) => rows.reduce((a, r) => a + (r[k] || 0), 0);
function filterByChip(d, chip) {
  if (!d || !chip || chip === "all") return d;
  if (d.tab === "works" && WORK_FILTERS[chip]) {
    const works = d.works.filter(WORK_FILTERS[chip]);
    return { ...d, works, total: sumOf(works, "amount"), held: sumOf(works, "held") };
  }
  if (d.tab === "lines" && LINE_FILTERS[chip] && d.count <= d.lines.length) {
    const lines = d.lines.filter(LINE_FILTERS[chip]);
    return { ...d, lines, count: lines.length, total: sumOf(lines, "amount"), usages: sumOf(lines, "usages") };
  }
  return d;
}

/** Fetch every source's overview and tabs in the background, once per visit, so the first click on
 *  a tab or a source shows its content straight away instead of a loading box. */
const warmed = new Set();
function warmRoyalties(sources, tenantId) {
  const id = `${tenantId}:${sources.join(",")}`;
  if (!tenantId || warmed.has(id)) return;
  warmed.add(id);
  const key = (k) => cacheKey(`royalties:${k}`, tenantId);
  setTimeout(async () => {
    for (const src of sources) {
      const jobs = [
        [`ov:${src}:`, "/royalties/overview", {}],
        [`tab:${src}::works`, "/royalties/works", {}, "works"],
        [`tab:${src}::lines`, "/royalties/lines", { limit: 5000 }, "lines"],
        [`tab:${src}::writers`, "/royalties/writers", {}, "writers"],
        [`tab:${src}::statements`, "/royalties/statements", {}, "statements"],
      ];
      for (const [k, url, extra, tab] of jobs) {
        if (cacheGet(key(k)) != null) continue;
        try {
          const { data } = await api.get(url, { params: { source: src, ...extra } });
          cacheSet(key(k), tab ? { tab, ...data } : data);
        } catch {
          /* the page loads it itself */
        }
      }
    }
  }, 700);
}

function typePill(r) {
  if (r.amount < -1e-12) return <Pill tone="bad">Taken back</Pill>;
  return r.backpay ? <Pill tone="warn">Back-pay</Pill> : <Pill tone="brand">Current</Pill>;
}

/** In the All sources list: say which source still needs linking or checking, and open it on click. */
function SourceMatch({ g, cols, onOpen }) {
  const todo = cols.find((c) => g.sources[c.source] && ["none", "title"].includes(g.sources[c.source].match));
  if (!todo) return <MatchPill w={g} />;
  const m = g.sources[todo.source].match;
  return (
    <button onClick={() => onOpen(g, todo)} title={`Open the ${todo.source} side to ${m === "none" ? "link it to a track" : "check the track it was matched to"}`}>
      <Pill tone={m === "none" ? "bad" : "warn"} className="hover:brightness-95">
        <Link2 className="w-3 h-3" /> {todo.source} · {m === "none" ? "not linked" : "check title"} →
      </Pill>
    </button>
  );
}

function MatchPill({ w }) {
  if (w.match === "none" || !w.track) return <Pill tone="bad">Not linked</Pill>;
  if (w.match === "title")
    return (
      <Pill tone="warn">
        <Link2 className="w-3 h-3" /> By title — check
      </Pill>
    );
  return (
    <Pill tone="good">
      <Link2 className="w-3 h-3" /> Linked
    </Pill>
  );
}

// ------------------------------------------------------------------ work drawer
/** One recording on a work, with the Dispute / Release controls. */
function RecordingRow({ r, code, reasons, resolveHow, canEdit, onChanged }) {
  const [form, setForm] = useState(false);
  const [reason, setReason] = useState(reasons?.[0] || "");
  const [note, setNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [how, setHow] = useState(resolveHow?.[0] || "");
  const [busy, setBusy] = useState(false);
  const d = r.dispute;
  const resolved = d?.status === "resolved";
  const go = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(msg);
      setForm(false);
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  const mark = () => go(() => api.post(`/royalties/works/${encodeURIComponent(code)}/disputes`, { isrc: r.isrc, reason, note }), `Disputed — ${r.artist || "this recording"}'s money is held out of payouts`);
  const release = () => go(() => api.delete(`/royalties/works/${encodeURIComponent(code)}/disputes/${encodeURIComponent(r.isrc)}`), "Released — that money counts toward payouts again");
  const resolve = () =>
    go(async () => {
      await api.post(`/royalties/works/${encodeURIComponent(code)}/disputes/${encodeURIComponent(r.isrc)}/resolve`, { how, note });
      setResolving(false);
      setNote("");
    }, `Resolved — ${r.artist || "this recording"}'s money stays out of payouts for good`);
  const reopen = () => go(() => api.post(`/royalties/works/${encodeURIComponent(code)}/disputes/${encodeURIComponent(r.isrc)}/reopen`), "Reopened — back on the Disputed list");
  return (
    <div className={cn("rounded-xl border p-3 text-[12.5px]", resolved ? "border-line bg-surface" : d ? "border-gold bg-gold-light/60" : "border-line")}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div>
            <span className="font-semibold">{r.rec_title}</span> — {r.artist}
          </div>
          <div className="font-mono text-[11px] text-muted2 break-words">
            {r.isrc} {r.product ? `· ${r.product}` : ""} {r.distributor ? `· ${r.distributor}` : ""}
          </div>
          <div className="font-mono text-[11px] text-muted2">
            {r.usages} plays · {money(r.amount)}
          </div>
        </div>
        {resolved ? (
          <Pill>Not ours — resolved</Pill>
        ) : d ? (
          <Pill tone="warn">
            <PauseCircle className="w-3 h-3" /> Disputed · held
          </Pill>
        ) : r.is_track ? (
          <Pill tone="good">Your recording</Pill>
        ) : (
          canEdit &&
          r.isrc &&
          !form && (
            <Button size="sm" variant="outline" className="text-gold border-gold/60" onClick={() => setForm(true)}>
              <Flag className="w-3.5 h-3.5 mr-1" /> Dispute
            </Button>
          )
        )}
      </div>
      {form && (
        <div className="mt-3 space-y-2">
          <label className="block">
            <span className="block text-[12px] font-semibold mb-1">What's wrong?</span>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full h-9 rounded-lg border border-line bg-card px-2 text-[13px]">
              {reasons.map((x) => (
                <option key={x}>{x}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-[12px] font-semibold mb-1">Note for your team (optional)</span>
            <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Dispute opened on the MLC portal" className="w-full h-9 rounded-lg border border-line bg-card px-3 text-[13px]" />
          </label>
          <div className="flex gap-2">
            <Button size="sm" disabled={busy} className="bg-brand hover:bg-brand-hover" onClick={mark}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Mark disputed"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setForm(false)}>
              Cancel
            </Button>
          </div>
          <p className="text-[11.5px] text-muted2">Its money is held out of payouts — on this statement and every later one with this ISRC. The work stays linked.</p>
        </div>
      )}
      {resolved && (
        <div className="mt-2">
          <div>
            <span className="font-semibold">{d.how}</span>
            {d.resolve_note ? ` — ${d.resolve_note}` : ""}
          </div>
          <div className="text-[11.5px] text-muted2">
            Disputed {formatDate(d.created_at)} · resolved {formatDate(d.resolved_at)} by {d.resolved_by}. {money(r.amount)} in this view isn't counted — if it turns up on another statement it's left out too.
          </div>
          {canEdit && (
            <button disabled={busy} onClick={reopen} className="mt-1.5 text-[12px] font-semibold text-brand">
              Reopen
            </button>
          )}
        </div>
      )}
      {d && !resolved && (
        <div className="mt-2">
          <div>
            <span className="font-semibold">{d.reason}</span>
            {d.note ? ` — ${d.note}` : ""}
          </div>
          <div className="text-[11.5px] text-muted2">
            Marked {formatDate(d.created_at)} by {d.created_by}. {money(r.amount)} held in this view.
          </div>
          {canEdit && !resolving && (
            <div className="flex gap-2 flex-wrap mt-2">
              <Button size="sm" disabled={busy} className="bg-brand hover:bg-brand-hover" onClick={() => setResolving(true)}>
                Mark resolved — not ours
              </Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={release}>
                It's ours after all — release
              </Button>
            </div>
          )}
          {resolving && (
            <div className="mt-3 space-y-2">
              <label className="block">
                <span className="block text-[12px] font-semibold mb-1">How was it sorted?</span>
                <select value={how} onChange={(e) => setHow(e.target.value)} className="w-full h-9 rounded-lg border border-line bg-card px-2 text-[13px]">
                  {(resolveHow || []).map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-[12px] font-semibold mb-1">Note (optional)</span>
                <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="e.g. MLC case #48213 closed Nov 3" className="w-full h-9 rounded-lg border border-line bg-card px-3 text-[13px]" />
              </label>
              <div className="flex gap-2">
                <Button size="sm" disabled={busy} className="bg-brand hover:bg-brand-hover" onClick={resolve}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Mark resolved"}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setResolving(false)}>
                  Cancel
                </Button>
              </div>
              <p className="text-[11.5px] text-muted2">Its money stays out of payouts for good — past statements and any later one it turns up on. The work leaves the Disputed list.</p>
            </div>
          )}
          {!resolving && <p className="text-[11.5px] text-muted2 mt-1.5">If the MLC takes the money back, it arrives as negative lines on this recording and is held too, so it evens out by itself.</p>}
        </div>
      )}
    </div>
  );
}

/**
 * "Which Kingston is this?" — when more than one track in the catalog has the work's title, list them
 * all with what tells them apart (artist, release, ISRC) and what decides the money (writers, splits).
 */
function ChooseTrack({ w, t, onLink, onCancel, onSearchAll }) {
  const suggested = w.track && w.match !== "none" ? w.track.track_id : "";
  const [pick, setPick] = useState(suggested || "");
  const title = titleCase(w.title);
  const share = Number(w.payable || 0);
  const pickedTitle = w.candidates.find((c) => c.track_id === pick)?.title;
  return (
    <div className="mt-4">
      <div className="text-[15px] font-semibold">Which {title} is this?</div>
      <p className="text-[12.5px] text-muted2 mt-0.5">
        {w.candidates.length} tracks in your catalog are called “{title}”. Pick the one this money belongs to — its writers are who get paid.
      </p>
      <div className="mt-2 space-y-2" role="radiogroup" aria-label={`Which ${title} is this?`}>
        {w.candidates.map((c) => {
          const on = pick === c.track_id;
          const close = c.signed_share != null && Math.abs(c.signed_share - share) <= 2;
          return (
            <label key={c.track_id} className={cn("flex gap-3 items-start rounded-xl border p-3 cursor-pointer", on ? "border-brand bg-brand-light/50 ring-1 ring-brand" : "border-line hover:border-brand-ring")}>
              <input type="radio" name="which" checked={on} onChange={() => setPick(c.track_id)} className="mt-1 w-4 h-4 accent-[hsl(var(--brand))] shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block">
                  <span className="text-[13.5px] font-semibold">{c.title}</span>
                  <span className="text-[13px] text-muted2 ml-1.5">{c.artist || "no artist"}</span>
                  {c.track_id === suggested && (
                    <Pill tone="warn" className="ml-2 align-middle">
                      Suggested
                    </Pill>
                  )}
                </span>
                <span className="block font-mono text-[11px] text-muted2 mt-0.5">
                  {c.release_date ? `Released ${formatDate(c.release_date)}` : c.released === "Yes" ? "Released" : "Not released"} · ISRC {c.isrc || "—"}
                </span>
                <span className="flex flex-wrap gap-1.5 mt-2">
                  {c.writers.length ? (
                    c.writers.map((x, i) => (
                      <span key={i} className={cn("text-[12px] rounded-md border px-2 py-0.5 whitespace-nowrap", x.signed ? "border-line bg-card" : "border-dashed border-line text-muted2")}>
                        {x.name}
                        {x.share != null && <b className="font-mono font-medium ml-1">{Number(x.share)}%</b>}
                        {!x.signed && " · co-writer"}
                      </span>
                    ))
                  ) : (
                    <span className="text-[12px] text-muted2">No writers on this track yet</span>
                  )}
                </span>
                {c.writers.length > 0 && (
                  <span className={cn("block text-[12px] mt-1.5", close ? "text-stock-in" : "text-gold")}>
                    {c.signed_share == null
                      ? "No splits filled in for your writers on this track."
                      : `Your writers' splits here add up to ${c.signed_share}% — ${t.org} shows your share as ${share}%${close ? " ✓" : ""}`}
                  </span>
                )}
              </span>
            </label>
          );
        })}
        <label className={cn("flex gap-3 items-start rounded-xl border p-3 cursor-pointer", pick === "none" ? "border-brand bg-brand-light/50 ring-1 ring-brand" : "border-line hover:border-brand-ring")}>
          <input type="radio" name="which" checked={pick === "none"} onChange={() => setPick("none")} className="mt-1 w-4 h-4 accent-[hsl(var(--brand))] shrink-0" />
          <span>
            <span className="block text-[13.5px] font-semibold">None of these</span>
            <span className="block text-[12px] text-muted2">Leave it unlinked — it stays under Not assigned in Payouts.</span>
          </span>
        </label>
      </div>
      <div className="flex items-center gap-2 flex-wrap mt-3">
        <Button size="sm" disabled={!pick} className="bg-brand hover:bg-brand-hover" onClick={() => onLink(pick === "none" ? "" : pick)}>
          {pick === "none" ? "Leave unlinked" : pick ? `Link to ${pickedTitle}` : "Pick one"}
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <button onClick={onSearchAll} className="text-[12px] text-brand font-semibold ml-auto">
          Search the whole catalog instead
        </button>
      </div>
    </div>
  );
}

function WorkSheet({ code, params, open, onOpenChange, canEdit, onChanged, startLinking, t = termsFor("MLC") }) {
  const [w, setW] = useState(null);
  const [linking, setLinking] = useState(false);
  const [searchAll, setSearchAll] = useState(false);
  const load = useCallback(async () => {
    if (!code) return;
    try {
      const { data } = await api.get(`/royalties/works/${encodeURIComponent(code)}`, { params });
      setW(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [code, params]);
  useEffect(() => {
    setW(null);
    setLinking(!!startLinking);
    setSearchAll(false);
    load();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const link = async (trackId) => {
    try {
      await api.patch(`/royalties/works/${encodeURIComponent(code)}/match`, { track_id: trackId });
      toast.success(trackId ? (t.bmi ? "Linked — the BMI song number is saved on the track so the next report links by itself" : t.mri ? "Linked — the statement's ISRC is saved on the track so the next statement links by itself" : "Linked — the MLC's ISRC is saved on the track so next month matches by itself") : `Unlinked — it won't be linked to ${w?.track?.title || "that track"} again unless you link it yourself`);
      setLinking(false);
      setSearchAll(false);
      load();
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const services = w ? [...new Set(w.line_items.map((l) => serviceName(l.dsp)))] : [];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[680px] p-0 bg-card flex flex-col gap-0">
        {!w ? (
          <div className="p-10 flex justify-center">
            <SheetTitle className="sr-only">Loading work</SheetTitle>
            <SheetDescription className="sr-only">Loading</SheetDescription>
            <Loader2 className="w-5 h-5 animate-spin text-brand" />
          </div>
        ) : (
          <>
            <div className="p-6 pb-5 border-b border-line pr-12">
              <div className="eyebrow text-[10.5px]">{t.bmi ? `Song · BMI song number ${w.song_code}` : t.mri ? `Song · MRI song ID ${shownCode(w.song_code)}` : `Work · MLC song code ${w.song_code}`}</div>
              <SheetTitle className="font-display text-[24px] font-semibold leading-tight mt-1.5">{titleCase(w.title)}</SheetTitle>
              <SheetDescription className="text-[13px] text-muted2 mt-1">
                {t.bmi ? "Performance royalties" : w.writers.map((x) => x.name).join(", ") || "No writers listed"} · your share {Number(w.payable || 0)}%{w.iswc ? ` · ISWC ${w.iswc}` : ""}
              </SheetDescription>
              {canEdit && w.candidates?.length > 1 && (w.match === "title" || linking) && !searchAll ? (
                <ChooseTrack
                  key={`${w.song_code}:${w.track?.track_id || ""}`}
                  w={w}
                  t={t}
                  onLink={(id) => (id || w.track ? link(id) : setLinking(false))}
                  onCancel={linking ? () => setLinking(false) : null}
                  onSearchAll={() => {
                    setSearchAll(true);
                    setLinking(true);
                  }}
                />
              ) : (
              <>
              {w.track && w.match !== "none" ? (
                <div className="mt-4 rounded-xl border border-line p-3 flex items-center gap-3">
                  <span className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", w.match === "title" ? "bg-gold-light text-gold" : "bg-emerald-50 dark:bg-emerald-950/40 text-stock-in")}>
                    <Link2 className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] truncate">
                      <span className="font-semibold">{w.track.title}</span>
                      <span className="text-muted2 ml-1.5">{w.track.artist || "no artist"}</span>
                    </div>
                    <div className="font-mono text-[11px] text-muted2 truncate">
                      {w.match === "title" ? (t.bmi ? "More than one track has this title — check it's the right one" : "Matched by title only — the MLC has ISRC " + (w.isrc || "—")) : `ISRC ${w.track.isrc || "—"}`}
                    </div>
                  </div>
                  {canEdit && w.match === "title" && (
                    <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => link(w.track.track_id)}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Same song
                    </Button>
                  )}
                  {canEdit && (
                    <Button size="sm" variant="outline" onClick={() => setLinking((v) => !v)}>
                      Change
                    </Button>
                  )}
                  <Link to={`/catalog/${w.track.track_id}`} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand whitespace-nowrap">
                    Open <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-stock-out/40 bg-rose-50/50 dark:bg-rose-950/20 p-3">
                  <div className="text-[13px] font-semibold">Not linked to a track in your catalog</div>
                  <div className="text-[12px] text-muted2 mt-0.5">{t.bmi ? "BMI reports carry no ISRC, so songs are linked by title" : `ISRC on the statement: ${w.isrcs.join(", ") || "none"}`}</div>
                  {canEdit && !linking && (
                    <Button size="sm" variant="outline" className="mt-2" onClick={() => setLinking(true)}>
                      Link to a track
                    </Button>
                  )}
                </div>
              )}
              {linking && (
                <div className="mt-3">
                  <TrackPicker autoFocus onPick={(t) => link(t.track_id)} />
                  {w.track && (
                    <div className="mt-2.5 rounded-lg border border-line p-2.5">
                      <button onClick={() => link("")} className="text-[12.5px] font-semibold text-stock-out">
                        Not our song — unlink from {w.track.title}
                      </button>
                      <p className="text-[11.5px] text-muted2 mt-0.5">
                        {t.bmi
                          ? "Clears the BMI song number from the track, and future reports won't link this song to it."
                          : "Forgets the statement's ISRC on the track (its own ISRC stays), and future statements won't link this work to it. Link it again any time to undo."}
                      </p>
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3 border-b border-line">
                {[
                  [t.paid, <Money key="m" value={w.amount} bold className="text-[16px]" places={t.bmi ? 2 : undefined} />],
                  [t.Plays, w.usages.toLocaleString()],
                  ...(w.not_ours && !w.held
                    ? [
                        ["Counts to payouts", <Money key="c" value={w.amount - w.not_ours} places={Math.abs(w.amount - w.not_ours) < 1e-9 ? 2 : undefined} />],
                        ["Not ours · resolved", <Money key="n" value={w.not_ours} className="text-muted2" />],
                      ]
                    : w.held
                    ? [
                        ["Counts to payouts", <Money key="c" value={w.amount - w.held - (w.not_ours || 0)} places={Math.abs(w.amount - w.held - (w.not_ours || 0)) < 1e-9 ? 2 : undefined} />],
                        ["Held · disputed", <Money key="h" value={w.held} className="text-gold" />],
                      ]
                    : [
                        ["Lines", w.lines],
                        [t.Services, services.join(", ")],
                      ]),
                ].map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <div className="eyebrow text-[10px]">{k}</div>
                    <div className="text-[15px] font-semibold mt-1 truncate">{v}</div>
                  </div>
                ))}
              </div>
              <div className="px-6 py-4 border-b border-line">
                <div className="eyebrow mb-2">{t.bmi ? "When it was performed" : t.mri ? "When the videos were made" : "When the plays happened"}</div>
                <Bars labelWidth={80} places={t.bmi ? 2 : undefined} items={w.by_period.map((p) => ({ key: p.period, label: monthLabel(p.period), amount: p.amount, sub: `${p.usages.toLocaleString()} ${t.plays}`, tone: p.current ? "current" : "back" }))} />
                {w.by_period.some((p) => !p.current) && (
                  <p className="text-[12px] text-muted2 mt-2">Gold is back-pay — money for older plays the services corrected. Red is money taken back.</p>
                )}
              </div>
              {w.recordings.length > 0 && (
                <div className="px-6 py-4 border-b border-line">
                  <div className="eyebrow mb-2">Recordings on the statement</div>
                  {t.bmi ? (
                    <div className="space-y-2">
                      {w.recordings.map((r) => (
                        <div key={r.isrc || r.rec_title} className="text-[12.5px]">
                          <span className="font-semibold">{r.rec_title}</span> — {r.artist}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {w.recordings.map((r) => (
                        <RecordingRow
                          key={r.isrc || r.rec_title}
                          r={r}
                          code={w.song_code}
                          reasons={w.dispute_reasons || []}
                          resolveHow={w.resolve_how || []}
                          canEdit={canEdit && !!w.track}
                          onChanged={() => {
                            load();
                            onChanged();
                          }}
                        />
                      ))}
                      {w.dispute_log?.some((d) => d.status === "released") && (
                        <div className="text-[11.5px] text-muted2">
                          {w.dispute_log
                            .filter((d) => d.status === "released")
                            .slice(0, 3)
                            .map((d) => (
                              <div key={d.dispute_id}>
                                {d.isrc} was disputed {formatDate(d.created_at)} and released {formatDate(d.released_at)} by {d.released_by}
                              </div>
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="eyebrow px-6 pt-4 pb-2">Lines · newest first</div>
              <div className="overflow-x-auto">
                <table className={cn("w-full", !t.bmi && "min-w-[560px]")}>
                  <thead>
                    <tr className="bg-surface/70 border-y border-line">
                      {(t.bmi ? ["Quarter", "Source", "Performances", t.paid] : ["Month", "Service", "Plan", "Type", "Plays", "Paid"]).map((h) => (
                        <th key={h} className={cn("t-head px-4 py-2 font-medium", h === t.paid || h === "Paid" ? "text-right" : "text-left")}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {w.line_items.map((l) => (
                      <tr key={`${l.statement_id}-${l.n}`} className={cn("border-b border-line", (l.held || l.not_ours) && "text-muted2")}>
                        <td className="px-4 py-2 text-[12.5px] whitespace-nowrap">{monthLabel(l.period)}</td>
                        <td className="px-4 py-2 text-[12.5px] whitespace-nowrap">{serviceName(l.dsp)}</td>
                        {!t.bmi && <td className="px-4 py-2 text-[12.5px] text-muted2">{l.offering || "—"}</td>}
                        {!t.bmi && (
                          <td className="px-4 py-2 whitespace-nowrap">
                            {typePill(l)}
                            {l.held && (
                              <Pill tone="warn" className="ml-1">
                                Held
                              </Pill>
                            )}
                            {l.not_ours && <Pill className="ml-1">Not ours</Pill>}
                          </td>
                        )}
                        <td className="px-4 py-2 font-mono text-[12.5px]">{l.usages}</td>
                        <td className="px-4 py-2 text-right text-[12.5px]">
                          <Money value={l.amount} places={t.bmi ? 2 : undefined} className={l.held ? "line-through decoration-gold" : l.not_ours ? "line-through" : undefined} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

/** Music Reports held this statement until the balance reached its minimum:
 *  Earned → Held → Paid (or "Not paid yet" while it's still waiting). */
function HeldJourney({ st, onOpen }) {
  const paid = !!st.paid_on;
  const months = (st.label || "").split(" · ").slice(1).join(" · ");
  const Step = ({ dot, title, children }) => (
    <div className="relative flex flex-col items-center text-center px-1.5">
      {dot}
      <div className="text-[13.5px] font-semibold mt-2">{title}</div>
      <div className="text-[12px] text-muted2 leading-snug">{children}</div>
    </div>
  );
  return (
    <section className="panel px-5 py-4 mb-4" aria-label="Where this money went">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">Where this money went</div>
          <div className="font-display text-[18px] font-semibold mt-0.5">
            {money(st.total)} · {st.works === 1 ? "1 song" : `${st.works} songs`}
          </div>
        </div>
        {paid ? (
          <Pill tone="good">
            <Check className="w-3.5 h-3.5" /> Paid {formatDate(st.paid_on)}
          </Pill>
        ) : (
          <Pill tone="warn">Held · not paid yet</Pill>
        )}
      </div>
      <div className="relative grid grid-cols-3 mt-4">
        <div className={cn("absolute top-[19px] left-[16.66%] right-[50%] h-[3px]", paid ? "bg-stock-in" : "bg-gold/50")} aria-hidden />
        <div className={cn("absolute top-[19px] left-[50%] right-[16.66%] h-[3px]", paid ? "bg-stock-in" : "bg-line")} aria-hidden />
        <Step dot={<span className="relative z-10 w-10 h-10 rounded-full grid place-items-center bg-brand-light text-brand ring-4 ring-card"><Disc3 className="w-[18px] h-[18px]" /></span>} title="Earned">
          {months || formatDate(st.distribution_date)}
        </Step>
        <Step dot={<span className="relative z-10 w-10 h-10 rounded-full grid place-items-center bg-gold-light text-gold ring-4 ring-card"><PauseCircle className="w-[18px] h-[18px]" /></span>} title="Held">
          Under the {money(st.threshold || 50, { places: 0 })} minimum
        </Step>
        {paid ? (
          <Step dot={<span className="relative z-10 w-10 h-10 rounded-full grid place-items-center bg-stock-in text-white ring-4 ring-card"><Check className="w-[18px] h-[18px]" /></span>} title="Paid">
            {formatDate(st.paid_on)}
            {st.paid_by && (
              <>
                {" · with "}
                <button onClick={() => onOpen(st.paid_by)} className="font-semibold text-brand underline underline-offset-2">
                  {(st.paid_by_label || "a later statement").split(" · ").slice(-1)[0]}
                </button>
              </>
            )}
          </Step>
        ) : (
          <Step dot={<span className="relative z-10 w-10 h-10 rounded-full grid place-items-center bg-surface text-muted2 border-2 border-dashed border-line ring-4 ring-card">…</span>} title="Not paid yet">
            {money(st.total)} of {money(st.threshold || 50, { places: 2 })}
          </Step>
        )}
      </div>
    </section>
  );
}

/** A Music Reports statement that also paid money held from earlier statements:
 *  one bar, green for its own songs and shades of gold for each held statement. */
function PaidTogether({ st, onOpen }) {
  let parts = (st.carried || []).map((c) => ({ ...c }));
  if (parts.length > 3) {
    // keep the bar readable: the two most recent, then everything older together
    const sorted = [...parts].sort((a, b) => (b.earned || "").localeCompare(a.earned || ""));
    const older = sorted.slice(2);
    parts = [...sorted.slice(0, 2), { statement_id: "", label: `${older.length} earlier statements`, amount: older.reduce((a, c) => a + c.amount, 0), works: older.reduce((a, c) => a + (c.works || 0), 0) }];
  }
  const shades = ["hsl(var(--gold))", "color-mix(in srgb, hsl(var(--gold)) 60%, hsl(var(--card)))", "color-mix(in srgb, hsl(var(--gold)) 35%, hsl(var(--card)))"];
  const rows = [{ own: true, label: `This statement · ${st.works === 1 ? "1 song" : `${st.works} songs`}`, amount: st.total, color: "hsl(var(--stock-in))" }].concat(
    parts.map((c, i) => ({ ...c, color: shades[i] || shades[2] }))
  );
  const total = rows.reduce((a, r) => a + r.amount, 0) || 1;
  const short = (label) => (label || "").split(" · ").slice(-1)[0];
  return (
    <section className="panel px-5 py-4 mb-4" aria-label="What Music Reports paid">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="eyebrow">What Music Reports paid you{st.distribution_date ? ` · ${formatDate(st.distribution_date)}` : ""}</div>
          <div className="font-display text-[18px] font-semibold mt-0.5">{money(total)}</div>
        </div>
        <Pill tone="good">
          <Check className="w-3.5 h-3.5" /> Paid
        </Pill>
      </div>
      <div className="flex h-6 rounded-lg overflow-hidden mt-3.5" aria-hidden>
        {rows.map((r, i) => (
          <span key={i} style={{ width: `${(r.amount / total) * 100}%`, background: r.color }} className={cn("block h-full min-w-[3px]", i > 0 && "border-l-2 border-card")} />
        ))}
      </div>
      <div className="mt-3.5 space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2.5 text-[13.5px]">
            <span className="w-3 h-3 rounded-[3px] shrink-0" style={{ background: r.color }} />
            <span className="min-w-0">
              {r.own ? (
                r.label
              ) : r.statement_id ? (
                <>
                  Held from{" "}
                  <button onClick={() => onOpen(r.statement_id)} className="font-semibold text-brand underline underline-offset-2">
                    {short(r.label)}
                  </button>
                  {r.works ? ` · ${r.works === 1 ? "1 song" : `${r.works} songs`}` : ""}
                </>
              ) : (
                <>Held from {r.label.toLowerCase().startsWith("earlier statements not") ? <span className="text-muted2">earlier statements you haven't imported</span> : r.label}</>
              )}
            </span>
            <Money value={r.amount} bold className="ml-auto text-[13px]" />
          </div>
        ))}
        <div className="flex justify-between border-t border-line pt-2.5 mt-1 text-[13.5px] font-semibold">
          <span>Paid{st.distribution_date ? ` on ${formatDate(st.distribution_date)}` : ""}</span>
          <Money value={total} bold className="text-[13px]" />
        </div>
      </div>
    </section>
  );
}

function titleCase(s) {
  return (s || "").toLowerCase().replace(/(^|[\s(\-/])([a-z])/g, (m, a, b) => a + b.toUpperCase());
}

// ------------------------------------------------------------------ page
function SourceRoyalties({ source, switcher, onDataChanged }) {
  // What each source and tab last showed, so switching paints at once (see lib/cache.js).
  const { tenantId } = useTenant();
  const seen = useMemo(
    () => ({
      get: (k) => cacheGet(cacheKey(`royalties:${k}`, tenantId)),
      set: (k, v) => cacheSet(cacheKey(`royalties:${k}`, tenantId), v),
    }),
    [tenantId]
  );
  const { user } = useAuth();
  const canEdit = user?.role !== "writer";
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "works";
  const statement = params.get("statement") || "";
  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const [q, setQ] = useState("");
  const [service, setService] = useState("");
  const [writer, setWriter] = useState(params.get("writer") || "");
  const [offering, setOffering] = useState("");
  const [range, setRange] = useState(EMPTY);
  const [chip, setChip] = useState(() => params.get("chip") || "all");
  const [moreFilters, setMoreFilters] = useState(false);
  const [mode, setMode] = useViewMode(`royalties-${tab}`, "list");
  const [overview, setOverview] = useState(() => seen.get(`ov:${source}:${statement}`) || null);
  const [options, setOptions] = useState({ services: [], offerings: [], writers: [] });
  const [data, setData] = useState(null);
  // ?work=CODE opens that work's panel (Payouts › Not assigned › "Link it" adds &link=1 to open the track picker)
  const [openCode, setOpenCode] = useState(() => params.get("work") || null);
  const startLinking = params.get("link") === "1";
  const [exporting, setExporting] = useState(false);
  const req = useRef(0);

  const filterParams = useMemo(
    () => ({ source, statement, q, service, writer, offering, ...rangeParams(range) }),
    [source, statement, q, service, writer, offering, range]
  );
  const drawerParams = useMemo(() => ({ source, statement, service, offering, ...rangeParams(range) }), [source, statement, service, offering, range]);

  const plain = !(q || service || writer || offering || rangeActive(range));
  const loadOverview = useCallback(async () => {
    try {
      const { data: d } = await api.get("/royalties/overview", { params: filterParams });
      setOverview(d);
      if (plain) seen.set(`ov:${source}:${statement}`, d);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [filterParams, plain, source, statement, seen]);

  // More than 5,000 lines is the one case the browser can't filter on its own — then the chip goes to the server.
  const [linesCut, setLinesCut] = useState(false);
  const serverChip = tab === "lines" && linesCut && chip !== "all";
  const chipForServer = serverChip ? chip : "";
  const loadTab = useCallback(async () => {
    const id = ++req.current;
    try {
      let d;
      if (tab === "works") d = (await api.get("/royalties/works", { params: filterParams })).data;
      else if (tab === "lines") d = (await api.get("/royalties/lines", { params: { ...filterParams, limit: 5000, ...(chipForServer ? { chip: chipForServer } : {}) } })).data;
      else if (tab === "writers") d = (await api.get("/royalties/writers", { params: filterParams })).data;
      else d = (await api.get("/royalties/statements")).data;
      if (id === req.current) setData({ tab, ...d });
      if (plain && !chipForServer) seen.set(`tab:${source}:${statement}:${tab}`, { tab, ...d });
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [tab, filterParams, chipForServer, plain, source, statement, seen]);

  useEffect(() => {
    const t = setTimeout(loadOverview, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadOverview, q]);
  useEffect(() => {
    const t = setTimeout(loadTab, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadTab, q]);
  useEffect(() => {
    api
      .get("/royalties/options", { params: { statement, source } })
      .then(({ data: d }) => setOptions(d))
      .catch(() => {});
  }, [statement, source]);
  // The list on screen is the whole list for these filters; chips narrow it in the browser (filterByChip),
  // so arriving on a chip — or switching one — paints at once.
  const base = data && data.tab === tab ? data : seen.get(`tab:${source}:${statement}:${tab}`) || null;
  const shown = serverChip ? base : filterByChip(base, chip);
  useEffect(() => {
    if (base?.tab === "lines" && !serverChip) setLinesCut(base.count > base.lines.length);
  }, [base, serverChip]);
  // Arriving from a link that picks a filter (Payouts › "See disputed works") opens
  // straight on the list: the overview is folded away until asked for.
  const [focus, setFocus] = useState(() => !!params.get("chip"));
  // Switching tab resets the chips, unless a stat card asked for a specific one.
  const nextChip = useRef(params.get("chip") || "");
  useEffect(() => {
    setChip(nextChip.current || "all");
    nextChip.current = "";
  }, [tab]);
  const goTo = (t, c) => {
    if (t === tab) setChip(c);
    else {
      nextChip.current = c;
      setParam("tab", t === "works" ? "" : t);
    }
  };

  const refresh = () => {
    loadOverview();
    loadTab();
  };

  const stmts = overview?.statements || [];
  const st = overview?.statement;
  const tot = overview?.totals;
  const filtersOn = !!(q || service || writer || offering || rangeActive(range));
  const clearAll = () => {
    setQ("");
    setService("");
    setWriter("");
    setOffering("");
    setRange(EMPTY);
    setChip("all");
  };

  const deleteStatement = async (s) => {
    const ok = await confirm({
      title: `Remove the ${s.label} statement?`,
      body: `Its ${s.line_count} lines come out of MetaTrack. Your catalog isn't touched, and you can import the files again any time.`,
      confirmText: "Remove statement",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/royalties/statements/${s.statement_id}`);
      toast.success("Statement removed");
      onDataChanged?.();
      if (statement === s.statement_id) setParam("statement", "");
      refresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const exportLines = async () => {
    setExporting(true);
    try {
      await downloadFile("/royalties/export.csv", "royalties.csv", { ...filterParams, chip: tab === "lines" ? chip : "all" });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setExporting(false);
    }
  };

  const pickPeriod = (p) => {
    const r = monthRange(p.key);
    setRange({ preset: "custom", field: "usage", ...r });
  };

  if (overview && !stmts.length) {
    return (
      <Layout title="Royalties" subtitle="What the MLC paid you, and for which songs">
        <div className="panel">
          <EmptyState
            icon={CircleDollarSign}
            title="No statements yet"
            body={
              canEdit
                ? "Download your statement from the MLC portal — work summary or royalty detail, all services or one file per service — and drop the files in. Duplicates are spotted for you."
                : "Nothing has been imported yet."
            }
            action={
              canEdit && (
                <Button asChild className="bg-brand hover:bg-brand-hover">
                  <Link to="/royalties/import">
                    <Upload className="w-4 h-4 mr-2" /> Import a statement
                  </Link>
                </Button>
              )
            }
          />
        </div>
      </Layout>
    );
  }

  const t = termsFor(source, st?.status);
  const openStatement = (id) => {
    setParam("statement", id === (stmts[0] && stmts[0].statement_id) ? "" : id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const chips = t.bmi ? (tab === "works" ? BMI_CHIPS : null) : tab === "works" ? WORK_CHIPS : tab === "lines" ? LINE_CHIPS : null;
  const tabs = TABS.filter(([k]) => !(t.bmi && k === "writers"));
  // the freshly loaded tab, else what this tab showed last time
  const counts = shown?.counts || {};
  const backYears = tot?.backpay_years || [];

  return (
    <Layout
      title="Royalties"
      subtitle={st ? `${t.mri ? `Music Reports · ${st.service || "statement"}` : `${st.source} · ${t.bmi ? "performance" : "mechanical"} royalties`} · ${st.payee_name || "—"}` : statement === "all" ? "Every statement together" : "What your songs earned"}
      action={
        <>
          <Button variant="outline" onClick={exportLines} disabled={exporting || !stmts.length}>
            {exporting ? <Loader2 className="w-4 h-4 animate-spin sm:mr-2" /> : <Download className="w-4 h-4 sm:mr-2" />}
            <span className="hidden sm:inline">Export</span>
          </Button>
          {canEdit && (
            <Button className="bg-brand hover:bg-brand-hover" onClick={() => navigate("/royalties/import")}>
              <Upload className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Import statement</span>
            </Button>
          )}
        </>
      }
    >
      {!overview ? (
        focus ? null : (
          <div className="min-h-[560px]">
            <LoadingPanel rows={6} />
          </div>
        )
      ) : (
        <>
          <div className="flex items-center gap-3 flex-wrap mb-4">
            {switcher}
            <label className="relative inline-flex items-center h-10 rounded-lg border border-line bg-card text-[13.5px] font-semibold">
              <CalendarDays className="w-4 h-4 absolute left-3 text-muted2 pointer-events-none" />
              <select
                aria-label="Statement"
                value={statement || st?.statement_id || ""}
                onChange={(e) => setParam("statement", e.target.value === (stmts[0] && stmts[0].statement_id) ? "" : e.target.value)}
                className="appearance-none bg-transparent h-full pl-9 pr-9 outline-none cursor-pointer"
              >
                {stmts.map((s) => (
                  <option key={s.statement_id} value={s.statement_id}>
                    {s.label} {s.status === "accrued" ? "accrual report" : s.status === "held" ? "statement · held" : "statement"}
                  </option>
                ))}
                {stmts.length > 1 && st?.status !== "accrued" && <option value="all">All statements together</option>}
              </select>
              <ArrowRight className="w-3.5 h-3.5 absolute right-3 rotate-90 text-muted2 pointer-events-none" />
            </label>
            {st?.distribution_date && <span className="text-[12.5px] text-muted2">{t.accrued || st.rollover ? "dated" : "paid"} {formatDate(st.distribution_date)}</span>}
            {st &&
              (st.agree ? (
                <Pill tone="good" className="h-7 px-2.5 text-[12px]">
                  <Check className="w-3.5 h-3.5" /> {t.bmi ? "Matches the totals printed on the PDF" : t.mri ? "Matches the totals printed on the statement" : "Totals check out against the MLC files"}
                </Pill>
              ) : (
                <Pill tone="warn" className="h-7 px-2.5 text-[12px]">
                  <AlertTriangle className="w-3.5 h-3.5" /> The files you imported didn't all agree — see Statements
                </Pill>
              ))}
            <div className="flex-1" />
            {st && (
              <span className="text-[12.5px] text-muted2 max-sm:hidden">
                {t.bmi ? "BMI account" : "Payee"} <b className="text-ink font-semibold">{st.payee_mlc || st.payee_name}</b>
                {st.payee_ipi ? ` · IPI ${st.payee_ipi}` : ""}
              </span>
            )}
          </div>

          {focus && (
            <button onClick={() => setFocus(false)} className="text-[12.5px] font-semibold text-brand mb-1">
              Show the overview — totals and charts
            </button>
          )}
          {!focus && t.accrued && (
            <section className="rounded-2xl border border-gold/30 bg-gold-light px-5 py-4 mb-4 flex items-center gap-4 flex-wrap">
              <AlertTriangle className="w-5 h-5 text-gold shrink-0" />
              <div className="flex-1 min-w-[240px] text-[13.5px] leading-relaxed">
                <b>This money is earned but not paid yet.</b> An accrual report lists royalties {st.source} is holding because the total is under its payout level
                {st.threshold ? `. With direct deposit, ${st.source} pays once the total reaches ${money(st.threshold, { places: 2 })}.` : "."}
              </div>
              <Threshold amount={st.total} threshold={st.threshold} className="w-full sm:w-[280px]" />
            </section>
          )}
          {!focus && t.mri && st?.rollover && <HeldJourney st={st} onOpen={openStatement} />}
          {!focus && t.mri && st?.carried?.length > 0 && <PaidTogether st={st} onOpen={openStatement} />}
          {focus ? null : t.bmi ? (
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
              <StatCard label={filtersOn ? `${t.paid} · filtered` : t.paid} value={money(tot.amount, { places: 2 })} sub={`${tot.lines} lines on the report`} tone={t.accrued ? "warn" : "default"} icon={CircleDollarSign} />
              <StatCard label="Performances" value={tot.usages.toLocaleString()} sub={`Across ${tot.services} source${tot.services === 1 ? "" : "s"}`} icon={Disc3} />
              <StatCard
                label="Songs earning"
                value={tot.works}
                tone={tot.unmatched || tot.matched_title ? "warn" : "good"}
                sub={tot.unmatched || tot.matched_title ? `${tot.works - tot.unmatched - tot.matched_title} linked to your catalog · ${tot.matched_title + tot.unmatched} to check` : "All linked to your catalog"}
                icon={ListMusic}
                onClick={tot.unmatched || tot.matched_title ? () => goTo("works", "unmatched") : undefined}
              />
              {t.accrued && st.threshold ? (
                <StatCard label="Still to go" value={money(Math.max(0, st.threshold - st.total), { places: 2 })} sub={`Before ${st.source} pays this out`} icon={AlertTriangle} />
              ) : (
                <StatCard label="Sources" value={tot.services} sub="Services that reported plays" icon={Tag} />
              )}
            </div>
          ) : (
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-4">
            <StatCard
              label={filtersOn ? (st?.rollover ? "Earned · filtered" : "Paid · filtered") : st?.rollover ? "Earned this statement" : "Paid this statement"}
              value={money(tot.amount)}
              sub={tot.interest ? `Royalty ${money(tot.royalty)} + interest ${money(tot.interest)}` : `${tot.lines} lines`}
              icon={CircleDollarSign}
            />
            <StatCard label={t.mri ? "Creations counted" : "Plays counted"} value={tot.usages.toLocaleString()} sub={`Across ${tot.services} service${tot.services === 1 ? "" : "s"}`} icon={Disc3} />
            <StatCard
              label={t.mri ? "Songs paid" : "Works paid"}
              value={tot.works}
              tone={tot.unmatched || tot.matched_title ? "warn" : "good"}
              sub={
                tot.unmatched || tot.matched_title
                  ? `${tot.works - tot.unmatched - tot.matched_title} linked to your catalog · ${tot.matched_title + tot.unmatched} to check`
                  : "All linked to your catalog"
              }
              icon={ListMusic}
              onClick={tot.unmatched || tot.matched_title ? () => goTo("works", "unmatched") : undefined}
            />
            <StatCard
              label="Back-pay & corrections"
              value={money(tot.backpay_amount)}
              tone={tot.backpay_lines ? "warn" : "default"}
              sub={
                tot.backpay_lines
                  ? `${tot.backpay_lines} lines for ${backYears.length > 1 ? `${backYears[0]}–${backYears[backYears.length - 1]}` : backYears[0]}${tot.negative_works ? ` · ${tot.negative_works} works went negative` : ""}`
                  : "None on this statement"
              }
              icon={AlertTriangle}
              onClick={tot.backpay_lines ? () => goTo("lines", "backpay") : undefined}
            />
          </div>
          )}

          {!focus && (
          <div className="grid lg:grid-cols-2 gap-4 mb-2">
            <section className="panel p-5">
              <div className="flex items-center mb-3">
                <span className="eyebrow">By {t.service}</span>
                <span className="ml-auto text-[12px] text-muted2">{service ? <button onClick={() => setService("")} className="text-brand font-semibold">Show all</button> : "Click one to filter"}</span>
              </div>
              <Bars
                places={t.bmi ? 2 : undefined}
                activeKey={service}
                onPick={(i) => setService(service === i.key ? "" : i.key)}
                items={overview.by_service.map((s) => ({ key: s.name, label: serviceName(s.name), amount: s.amount, sub: `${s.usages.toLocaleString()} ${t.plays}` }))}
              />
            </section>
            <section className="panel p-5">
              <div className="flex items-center mb-3 gap-3 flex-wrap">
                <span className="eyebrow">{t.bmi ? "By quarter the songs were performed" : t.mri ? "By month the videos were made" : "By month the plays happened"}</span>
                <span className={cn("ml-auto flex gap-3 text-[11.5px] text-muted2", t.bmi && "hidden")}>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="w-2.5 h-2.5 rounded-sm bg-brand" /> Current
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="w-2.5 h-2.5 rounded-sm bg-gold" /> Back-pay
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <i className="w-2.5 h-2.5 rounded-sm bg-stock-out" /> Taken back
                  </span>
                </span>
              </div>
              <Bars
                places={t.bmi ? 2 : undefined}
                labelWidth={120}
                onPick={pickPeriod}
                activeKey={range.from && range.to && range.from.slice(0, 7) === range.to.slice(0, 7) ? range.from.slice(0, 7) : ""}
                items={overview.by_period.slice(0, 12).map((p) => ({
                  key: p.period,
                  label: monthLabel(p.period) + (p.current && !t.bmi ? " · current" : ""),
                  amount: p.amount,
                  sub: t.bmi ? `${p.usages.toLocaleString()} performances` : `${p.lines} line${p.lines === 1 ? "" : "s"}`,
                  tone: p.current ? "current" : "back",
                }))}
              />
              {overview.by_period.length > 12 && <p className="text-[12px] text-muted2 mt-2">+ {overview.by_period.length - 12} older months</p>}
            </section>
          </div>
          )}
        </>
      )}

      <div className="flex gap-5 border-b border-line mt-4 overflow-x-auto" role="tablist">
        {tabs.map(([k, l0]) => {
          const l = k === "works" ? t.Works : k === "statements" ? t.statements : l0;
          const n = k === "works" ? tot?.works : k === "lines" ? tot?.lines : k === "writers" ? tot?.writers : stmts.length;
          return (
            <button
              key={k}
              role="tab"
              aria-selected={tab === k}
              onClick={() => setParam("tab", k === "works" ? "" : k)}
              className={cn("pb-2.5 px-1 text-[13.5px] font-semibold border-b-2 -mb-px whitespace-nowrap", tab === k ? "border-brand text-brand" : "border-transparent text-muted2 hover:text-ink")}
            >
              {l}
              {n != null && <span className="font-normal"> · {n}</span>}
            </button>
          );
        })}
      </div>

      {tab !== "statements" && (
        <StickyFilters className="space-y-3 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="relative flex-1 min-w-[200px] max-w-[280px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={tab === "writers" ? "Writer name or IPI…" : t.bmi ? "Song title or BMI song number…" : "Work, ISRC, song code, artist…"}
                aria-label="Search royalties"
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring"
              />
            </label>
            <button
              type="button"
              onClick={() => setMoreFilters((v) => !v)}
              aria-expanded={moreFilters}
              className={cn("md:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[13px]", filtersOn ? "border-brand text-brand bg-brand-light" : "border-line bg-card")}
            >
              <SlidersHorizontal className="w-4 h-4" /> Filters
            </button>
            <div className={cn("contents", !moreFilters && "max-md:hidden")}>
              <FilterSelect value={service} onChange={setService} allLabel={`All ${t.service}s`} icon={Disc3} options={options.services.map((s) => ({ value: s, label: serviceName(s) }))} />
              {tab !== "writers" && !t.bmi && <FilterSelect value={writer} onChange={setWriter} allLabel="All writers" icon={Users} options={options.writers.map((w) => ({ value: w.key, label: w.name }))} />}
              {!t.bmi && <FilterSelect value={offering} onChange={setOffering} allLabel="All plans" icon={Tag} options={options.offerings.map((o) => ({ value: o }))} />}
              <DateRangeFilter value={range} onChange={setRange} fields={t.bmi ? BMI_DATE_FIELDS : DATE_FIELDS} />
            </div>
            <div className="flex-1" />
            <ViewToggle mode={mode} onChange={setMode} />
          </div>
          {(chips || filtersOn) && (
            <div className="flex items-center gap-2 flex-wrap">
              {chips && (
                <Chips
                  items={chips.filter(([key]) => key !== "disputed" || counts.disputed > 0 || chip === "disputed").map(([key, label]) => ({ key, label, count: counts[key] }))}
                  value={chip}
                  onChange={setChip}
                />
              )}
              {filtersOn && (
                <button onClick={clearAll} className="inline-flex items-center gap-1 text-[12.5px] text-muted2 hover:text-ink px-2">
                  <X className="w-3.5 h-3.5" /> Clear filters
                </button>
              )}
            </div>
          )}
        </StickyFilters>
      )}

      <div className="min-h-[70vh]">
      {!shown ? (
        <div className="mt-3">
          <LoadingPanel rows={6} />
        </div>
      ) : tab === "works" ? (
        <>
          {chip === "disputed" && shown.held !== undefined && shown.works?.length > 0 && (
            <div className="mt-1 mb-3 rounded-xl bg-gold-light px-4 py-2.5 text-[13px]">
              <b className="text-gold">
                <Money value={shown.held} />
              </b>{" "}
              held on {shown.works.length} disputed work{shown.works.length === 1 ? "" : "s"}{" "}
              {statement === "all" ? "across all your statements" : `on the ${st?.label || "selected"} statement`} — not in anyone's payouts until you release it. Open a work to see why, or to release it.
              {statement !== "all" && stmts.length > 1 && (
                <button onClick={() => setParam("statement", "all")} className="ml-2 font-semibold text-brand">
                  See all statements →
                </button>
              )}
            </div>
          )}
          <WorksView data={shown} mode={mode} onOpen={setOpenCode} t={t} />
        </>
      ) : tab === "lines" ? (
        <LinesView data={shown} mode={mode} onOpen={setOpenCode} t={t} />
      ) : tab === "writers" ? (
        <WritersView
          data={shown}
          mode={mode}
          onPick={(w) => {
            setWriter(w.key);
            setParam("tab", "");
          }}
        />
      ) : (
        <StatementsView data={{ ...shown, statements: shown.statements.filter((s) => s.source === source) }} canEdit={canEdit} onDelete={deleteStatement} onOpen={(s) => setParams({ source, statement: s.statement_id })} />
      )}

      </div>

      <WorkSheet
        code={openCode}
        params={drawerParams}
        t={t}
        open={!!openCode}
        startLinking={startLinking && openCode === params.get("work")}
        onOpenChange={(v) => {
          if (v) return;
          setOpenCode(null);
          if (params.get("work")) {
            const next = new URLSearchParams(params);
            next.delete("work");
            next.delete("link");
            setParams(next, { replace: true });
          }
        }}
        canEdit={canEdit}
        onChanged={refresh}
      />
    </Layout>
  );
}

// ------------------------------------------------------------------ tab views
function Empty({ body }) {
  return (
    <div className="panel mt-1">
      <EmptyState icon={FileSpreadsheet} title="Nothing matches" body={body || "Try clearing a filter or widening the dates."} />
    </div>
  );
}

function WorksView({ data, mode, onOpen, t }) {
  const works = data.works;
  if (!works.length) return <Empty />;
  if (t.bmi && mode !== "grid")
    return (
      <div className="panel overflow-hidden mt-1">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="bg-surface/70 border-b border-line">
                {["Song", "BMI song no.", "Share", "Sources", "Performances", t.paid, "In your catalog"].map((h) => (
                  <th key={h} className={cn("t-head px-4 py-2.5 font-medium", h === t.paid ? "text-right" : "text-left")}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {works.map((w) => (
                <tr key={w.song_code} className="t-row cursor-pointer" onClick={() => onOpen(w.song_code)}>
                  <td className="t-cell font-semibold max-w-[260px] truncate">{titleCase(w.title)}</td>
                  <td className="t-cell font-mono text-[12.5px] text-muted2">{w.song_code}</td>
                  <td className="t-cell font-mono text-[12.5px]">{Number(w.payable)}%</td>
                  <td className="t-cell">
                    <div className="flex flex-wrap gap-1 max-w-[280px]">
                      {w.services.map((s) => (
                        <span key={s} className="px-2 h-[22px] inline-flex items-center rounded-md bg-surface border border-line text-[11.5px] whitespace-nowrap">
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="t-cell font-mono text-[12.5px]">{w.usages.toLocaleString()}</td>
                  <td className="t-cell text-right text-[12.5px]">
                    <Money value={w.amount} places={2} />
                  </td>
                  <td className="t-cell">
                    <MatchPill w={w} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-between px-4 py-3 border-t border-line text-[12.5px] text-muted2 flex-wrap gap-2">
          <span>
            {works.length} song{works.length === 1 ? "" : "s"} · BMI rounds every line to the cent
          </span>
          <span>
            Total <Money value={data.total} bold className="text-ink" places={2} />
          </span>
        </div>
      </div>
    );
  if (mode === "grid")
    return (
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mt-1">
        {works.map((w) => (
          <button key={w.song_code} onClick={() => onOpen(w.song_code)} className="panel p-4 text-left hover:border-brand-ring transition-colors flex flex-col gap-2">
            <div className="flex justify-between gap-3 items-baseline">
              <div className="font-display text-[16px] font-semibold leading-tight">
                {titleCase(w.title)}
                {w.disputes?.length > 0 ? <DisputeTag /> : w.not_ours ? <NotOursTag /> : null}
              </div>
              <Money value={w.amount} className="text-[13px] shrink-0" bold places={t.bmi ? 2 : undefined} />
            </div>
            <div className="text-[12.5px] text-muted2 truncate">
              {t.bmi ? `BMI ${w.song_code}` : w.writers.map((x) => x.name).join(", ")} · {Number(w.payable)}%
            </div>
            <DisputeLine w={w} />
            <div className="flex flex-wrap gap-1">
              {w.services.slice(0, 3).map((s) => (
                <span key={s} className="px-2 h-[22px] inline-flex items-center rounded-md bg-surface border border-line text-[11.5px]">
                  {serviceName(s)}
                </span>
              ))}
            </div>
            <div className="flex items-center justify-between pt-2 mt-auto border-t border-line">
              <MatchPill w={w} />
              <span className="font-mono text-[11.5px] text-muted2">
                {w.usages.toLocaleString()} {t.plays}{w.backpay_lines ? ` · ${w.backpay_lines} back-pay` : ""}
              </span>
            </div>
          </button>
        ))}
      </div>
    );
  return (
    <div className="panel overflow-hidden mt-1">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px]">
          <thead>
            <tr className="bg-surface/70 border-b border-line">
              {[t.mri ? "Song" : "Work", "Writers", "Share", "Services", t.Plays, "Paid", "In your catalog"].map((h) => (
                <th key={h} className={cn("t-head px-4 py-2.5 font-medium", h === "Paid" ? "text-right" : "text-left")}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {works.map((w) => (
              <tr key={w.song_code} className="t-row cursor-pointer" onClick={() => onOpen(w.song_code)}>
                <td className="t-cell max-w-[280px]">
                  <div className="font-semibold truncate">
                    {titleCase(w.title)}
                    {w.backpay_lines > 0 && (
                      <span className="ml-2 align-middle font-mono text-[10px] text-gold bg-gold-light rounded px-1 py-px">+{w.backpay_lines} back-pay</span>
                    )}
                    {w.disputes?.length > 0 ? <DisputeTag /> : w.not_ours ? <NotOursTag /> : null}
                  </div>
                  <div className="font-mono text-[11px] text-muted2">
                    {shownCode(w.song_code)}
                    {w.isrc ? ` · ${w.isrc}` : ""}
                  </div>
                  <DisputeLine w={w} />
                </td>
                <td className="t-cell text-[12.5px] text-muted2 max-w-[220px] truncate">{w.writers.map((x) => x.name).join(", ")}</td>
                <td className="t-cell font-mono text-[12.5px]">{Number(w.payable)}%</td>
                <td className="t-cell">
                  <div className="flex flex-wrap gap-1 max-w-[220px]">
                    {w.services.slice(0, 3).map((s) => (
                      <span key={s} className="px-2 h-[22px] inline-flex items-center rounded-md bg-surface border border-line text-[11.5px] whitespace-nowrap">
                        {serviceName(s)}
                      </span>
                    ))}
                    {w.services.length > 3 && <span className="text-[11.5px] text-muted2">+{w.services.length - 3}</span>}
                  </div>
                </td>
                <td className="t-cell font-mono text-[12.5px]">{w.usages}</td>
                <td className="t-cell text-right text-[12.5px]">
                  <Money value={w.amount} />
                </td>
                <td className="t-cell">
                  <MatchPill w={w} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between px-4 py-3 border-t border-line text-[12.5px] text-muted2">
        <span>
          {works.length} work{works.length === 1 ? "" : "s"}
        </span>
        <span>
          Total <Money value={data.total} bold className="text-ink" />
        </span>
      </div>
    </div>
  );
}

function LinesView({ data, mode, onOpen, t }) {
  const lines = data.lines;
  if (!lines.length) return <Empty />;
  const footer = (
    <div className="flex justify-between px-4 py-3 border-t border-line text-[12.5px] text-muted2 flex-wrap gap-2">
      <span>
        {lines.length < data.count ? `Showing ${lines.length} of ${data.count}` : `${data.count}`} line{data.count === 1 ? "" : "s"} · {data.usages.toLocaleString()} {t.plays}
      </span>
      <span>
        Total <Money value={data.total} bold className="text-ink" places={t.bmi ? 2 : undefined} />
      </span>
    </div>
  );
  if (t.bmi && mode !== "grid")
    return (
      <div className="panel overflow-hidden mt-1">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="bg-surface/70 border-b border-line">
                {["Quarter", "Song", "BMI song no.", "Source", "Performances", "Share", t.paid].map((h) => (
                  <th key={h} className={cn("t-head px-4 py-2.5 font-medium", h === t.paid ? "text-right" : "text-left")}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={`${l.statement_id}-${l.n}`} className="t-row cursor-pointer" onClick={() => onOpen(l.song_code)}>
                  <td className="t-cell text-[12.5px] whitespace-nowrap">{monthLabel(l.period)}</td>
                  <td className="t-cell font-semibold text-[13px] max-w-[240px] truncate">{titleCase(l.title)}</td>
                  <td className="t-cell font-mono text-[12.5px] text-muted2">{l.song_code}</td>
                  <td className="t-cell text-[12.5px] whitespace-nowrap">{l.dsp}</td>
                  <td className="t-cell font-mono text-[12.5px]">{l.usages.toLocaleString()}</td>
                  <td className="t-cell font-mono text-[12.5px]">{Number(l.payable || 0)}%</td>
                  <td className="t-cell text-right text-[12.5px]">
                    <Money value={l.amount} places={2} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {footer}
      </div>
    );
  if (mode === "grid")
    return (
      <>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mt-1">
          {lines.map((l) => (
            <button key={`${l.statement_id}-${l.n}`} onClick={() => onOpen(l.song_code)} className="panel p-4 text-left hover:border-brand-ring transition-colors">
              <div className="flex justify-between gap-3 items-baseline">
                <div className="font-semibold text-[14px] truncate">{titleCase(l.title)}</div>
                <Money value={l.amount} className="text-[12.5px]" bold places={t.bmi ? 2 : undefined} />
              </div>
              <div className="text-[12.5px] text-muted2 mt-0.5 truncate">
                {serviceName(l.dsp)} · {t.bmi ? `${l.usages.toLocaleString()} performances` : l.offering || "—"}
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-[12px]">{monthLabel(l.period)}</span>
                {t.bmi ? <span className="font-mono text-[11.5px] text-muted2">{Number(l.payable || 0)}%</span> : typePill(l)}
              </div>
            </button>
          ))}
        </div>
        <div className="panel mt-3">{footer}</div>
      </>
    );
  return (
    <div className="panel overflow-hidden mt-1">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1080px]">
          <thead>
            <tr className="bg-surface/70 border-b border-line">
              {["Month", "Work", "Recording · ISRC", "Service", "Plan", "Type", "Plays", "Share", "Paid"].map((h) => (
                <th key={h} className={cn("t-head px-4 py-2.5 font-medium", h === "Paid" ? "text-right" : "text-left")}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={`${l.statement_id}-${l.n}`} className="t-row cursor-pointer" onClick={() => onOpen(l.song_code)}>
                <td className="t-cell text-[12.5px] whitespace-nowrap">{monthLabel(l.period)}</td>
                <td className="t-cell font-semibold text-[13px] max-w-[200px] truncate">{titleCase(l.title)}</td>
                <td className="t-cell max-w-[200px]">
                  <div className="text-[12.5px] truncate">{l.artist || l.rec_title || "—"}</div>
                  <div className="font-mono text-[11px] text-muted2">{l.isrc || "—"}</div>
                </td>
                <td className="t-cell text-[12.5px] whitespace-nowrap">{serviceName(l.dsp)}</td>
                <td className="t-cell text-[12.5px] text-muted2 max-w-[160px] truncate">{l.offering || "—"}</td>
                <td className="t-cell">{typePill(l)}</td>
                <td className="t-cell font-mono text-[12.5px]">{l.usages}</td>
                <td className="t-cell font-mono text-[12.5px]">{Number(l.payable || 0)}%</td>
                <td className="t-cell text-right text-[12.5px]">
                  <Money value={l.amount} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer}
    </div>
  );
}

function WritersView({ data, mode, onPick }) {
  const rows = data.writers;
  if (!rows.length) return <Empty />;
  const note = <p className="text-[12px] text-muted2 mt-3">Paid is the money on works each writer is credited on — the MLC pays the publisher, so a work with two writers counts for both.</p>;
  if (mode === "grid")
    return (
      <>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 mt-1">
          {rows.map((w) => (
            <button key={w.key} onClick={() => onPick(w)} className="panel p-4 text-left hover:border-brand-ring transition-colors">
              <div className="flex justify-between gap-3 items-baseline">
                <div className="font-display text-[16px] font-semibold truncate">{w.name}</div>
                <Money value={w.amount} bold className="text-[13px]" />
              </div>
              <div className="font-mono text-[11.5px] text-muted2 mt-0.5">{w.ipi ? `IPI ${w.ipi}` : "No IPI on the statement"}</div>
              <div className="flex items-center justify-between mt-3 text-[12px]">
                <span>
                  {w.works} work{w.works === 1 ? "" : "s"} · {w.usages} plays
                </span>
                {w.roster ? <Pill tone="good">On your roster</Pill> : <Pill>Not on roster</Pill>}
              </div>
            </button>
          ))}
        </div>
        {note}
      </>
    );
  return (
    <>
      <div className="panel overflow-hidden mt-1">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr className="bg-surface/70 border-b border-line">
                {["Writer", "IPI on statement", "Works", "Plays", "Paid on their works", "Roster"].map((h) => (
                  <th key={h} className={cn("t-head px-4 py-2.5 font-medium", h.startsWith("Paid") ? "text-right" : "text-left")}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.key} className="t-row cursor-pointer" onClick={() => onPick(w)} title="See their works">
                  <td className="t-cell font-semibold">{w.name}</td>
                  <td className="t-cell font-mono text-[12.5px]">{w.ipi || "—"}</td>
                  <td className="t-cell font-mono text-[12.5px]">{w.works}</td>
                  <td className="t-cell font-mono text-[12.5px]">{w.usages}</td>
                  <td className="t-cell text-right text-[12.5px]">
                    <Money value={w.amount} />
                  </td>
                  <td className="t-cell">
                    {w.roster ? (
                      <Link to={`/writers?open=${w.roster.writer_id}`} onClick={(e) => e.stopPropagation()} className="text-[12.5px] font-semibold text-brand">
                        {w.roster.name}
                      </Link>
                    ) : (
                      <span className="text-[12.5px] text-muted2">Not on roster</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {note}
    </>
  );
}

const KIND_LABEL = { detail: "Royalty detail", summary: "Work summary", accrual: "Accrual report" };

function StatementsView({ data, canEdit, onDelete, onOpen }) {
  const rows = data.statements;
  if (!rows.length) return <Empty body="No statements imported yet." />;
  return (
    <div className="space-y-4 mt-4">
      {rows.map((s) => {
        const used = s.files.filter((f) => f.role === "used").length;
        return (
          <section key={s.statement_id} className="panel overflow-hidden">
            <div className="flex items-center gap-3 px-5 py-4 flex-wrap">
              <div className="w-10 h-10 rounded-xl bg-brand-light flex items-center justify-center shrink-0">
                <CircleDollarSign className="w-5 h-5 text-brand" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[14.5px] font-semibold">
                  {s.source} · {s.label} {s.status === "accrued" && <Pill tone="warn" className="ml-1 align-middle">Earned, not paid yet</Pill>}
                  {s.status === "held" && <Pill tone="warn" className="ml-1 align-middle">Held · not paid yet</Pill>}
                  {s.rollover && s.paid_on && <Pill tone="good" className="ml-1 align-middle">Held → paid {formatDate(s.paid_on)}</Pill>}
                  {s.carried?.length > 0 && (
                    <Pill tone="good" className="ml-1 align-middle">
                      + {money(s.carried.reduce((a, c) => a + c.amount, 0))} held from earlier
                    </Pill>
                  )}
                </div>
                <div className="text-[12.5px] text-muted2">
                  Distribution {s.distribution_id}
                  {s.distribution_date ? ` · ${s.status === "accrued" || s.rollover ? "dated" : "paid"} ${formatDate(s.distribution_date)}` : ""} · imported {formatDateTime(s.imported_at)}
                  {s.imported_by ? ` by ${s.imported_by}` : ""}
                </div>
              </div>
              <div className="text-right">
                <Money value={s.total} bold className="text-[15px]" places={s.source === "BMI" ? 2 : undefined} />
                <div className="text-[12px] text-muted2">
                  {s.works} works · {s.line_count} lines
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => onOpen(s)}>
                View
              </Button>
              {canEdit && (
                <Button variant="outline" size="sm" className="text-stock-out" onClick={() => onDelete(s)}>
                  <Trash2 className="w-4 h-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Remove</span>
                </Button>
              )}
            </div>
            <div className="grid md:grid-cols-2 border-t border-line">
              <div className="px-5 py-4 md:border-r border-line">
                <div className="eyebrow mb-2">Files · {s.files.length}</div>
                <p className="text-[12.5px] text-muted2 mb-2">
                  {s.source === "BMI" ? `Read from the PDF${s.pages ? `, ${s.pages} page${s.pages === 1 ? "" : "s"}` : ""}.` : `Kept ${KIND_LABEL[s.kind].toLowerCase()} lines from ${used} file${used === 1 ? "" : "s"}; the rest said the same thing.`}
                </p>
                <div className="max-h-[180px] overflow-y-auto space-y-1">
                  {s.files.map((f) => (
                    <div key={f.filename} className="flex items-center gap-2 text-[12px]">
                      {f.role === "used" ? <Pill tone="good">Used</Pill> : f.role === "check" ? <Pill tone="brand">Check</Pill> : <Pill>Covered</Pill>}
                      <span className="truncate font-mono text-[11px]" title={f.filename}>
                        {f.filename}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="px-5 py-4">
                <div className="eyebrow mb-2">{s.source === "BMI" ? "Does it match the PDF's totals?" : "Do the versions agree?"}</div>
                {s.checks.map((c) => (
                  <div key={c.label} className="flex justify-between items-center py-1.5 border-t border-line first:border-t-0 text-[12.5px] gap-3">
                    <span>{c.label}</span>
                    <span className="inline-flex items-center gap-1.5">
                      {c.printed != null ? <span className="font-mono">{c.money ? money(c.total, { places: 2 }) : c.total.toLocaleString()}</span> : <Money value={c.total} places={8} />}
                      {(c.printed != null ? Math.abs(c.total - c.printed) < 0.005 : Math.abs(c.total - s.total) < 1e-6) ? <Check className="w-3.5 h-3.5 text-stock-in" /> : <AlertTriangle className="w-3.5 h-3.5 text-gold" />}
                    </span>
                  </div>
                ))}
                {s.replaced && <p className="text-[12px] text-muted2 mt-2">Imported again since the first time — it replaced the earlier copy rather than adding to it.</p>}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ every source together
const SOURCE_TONE = { MLC: "bg-sec-mech-light text-sec-mech", BMI: "bg-sec-pro-light text-sec-pro", MRI: "bg-sec-dist-light text-sec-dist" };

function SourceSwitch({ value, sources, onChange }) {
  const opts = [["all", "All sources"], ...sources.map((s) => [s.source, s.source === "MRI" ? "Music Reports" : `${s.source} · ${SOURCE_KIND[s.source] || "royalties"}`])];
  return (
    <div role="group" aria-label="Royalty source" className="inline-flex p-[3px] gap-0.5 rounded-xl border border-line bg-card max-w-full overflow-x-auto">
      {opts.map(([k, l]) => (
        <button
          key={k}
          type="button"
          aria-pressed={value === k}
          onClick={() => onChange(k)}
          className={cn("h-[34px] px-3.5 rounded-[9px] text-[13px] font-semibold whitespace-nowrap transition-colors", value === k ? "bg-brand text-white" : "text-muted2 hover:text-ink")}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function SourceTag({ source }) {
  return <span className={cn("font-mono text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md", SOURCE_TONE[source] || "bg-surface text-muted2")}>{source}</span>;
}

function AllSources({ info, switcher, onOpenSource, canEdit, onChanged }) {
  const navigate = useNavigate();
  const [mode, setMode] = useViewMode("royalties-all", "list");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const cols = info.sources;
  const songs = info.songs.filter((g) => !q || `${g.title} ${g.artist || ""}`.toLowerCase().includes(q.toLowerCase()));
  const amountOf = (g, c) => {
    const v = g.sources[c.source];
    if (!v) return null;
    return c.has_accrued && !c.has_paid ? v.accrued : v.paid + v.accrued;
  };
  const openSong = (g, c) => {
    const v = g.sources[c.source];
    if (v) setOpen({ code: v.song_code, source: c.source, accrued: c.has_accrued && !c.has_paid });
  };
  const sheetParams = useMemo(() => ({ source: open?.source, statement: open?.accrued ? "" : "all" }), [open]);
  const paidFrom = cols.filter((c) => c.has_paid).map((c) => c.source).join(" and ");
  const accFrom = cols.filter((c) => c.has_accrued);
  return (
    <Layout
      title="Royalties"
      subtitle="What your songs earned, from every source you've imported"
      action={
        canEdit && (
          <Button className="bg-brand hover:bg-brand-hover" onClick={() => navigate("/royalties/import")}>
            <Upload className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Import statement</span>
          </Button>
        )
      }
    >
      <div className="flex items-center gap-3 flex-wrap mb-4">
        {switcher}
        <div className="flex-1" />
        <span className="text-[12.5px] text-muted2">Paid and not-yet-paid money are never added together</span>
      </div>
      {/* the four totals as one slim strip */}
      <div className="panel grid grid-cols-2 xl:grid-cols-4 mb-4 overflow-hidden">
        {[
          ["Paid to you", money(info.paid), paidFrom ? `${paidFrom} · every statement` : "Nothing paid yet", "text-stock-in"],
          [
            "Earned, not paid yet",
            money(info.accrued, { places: 2 }),
            accFrom.length ? accFrom.map((c) => `${c.source}${c.threshold ? ` · held until ${money(c.threshold, { places: 2 })}` : ""}`).join("; ") : "Nothing being held",
            info.accrued ? "text-gold" : "",
          ],
          ["Songs earning", info.song_count, info.in_both ? `${info.in_both} at more than one source` : "Across every source", ""],
          ["Sources", cols.length, cols.map((c) => c.name).join(", "), ""],
        ].map(([k, v, sub, tone], i) => (
          <div key={k} className={cn("px-4 py-3 min-w-0 border-line", i % 2 === 1 && "border-l", i >= 2 && "max-xl:border-t", i >= 1 && "xl:border-l")}>
            <div className="eyebrow text-[10px]">{k}</div>
            <div className={cn("font-display text-[22px] font-semibold leading-tight mt-0.5", tone)}>{v}</div>
            <div className="text-[12px] text-muted2 truncate" title={sub}>
              {sub}
            </div>
          </div>
        ))}
      </div>

      {/* one compact card per source, three to a row on a wide screen */}
      <div className="grid md:grid-cols-2 2xl:grid-cols-3 gap-3 mb-6">
        {cols.map((c) => {
          const accruedOnly = c.has_accrued && !c.has_paid;
          return (
            <section key={c.source} className="panel px-4 py-3.5 flex flex-col gap-2.5">
              <div className="flex items-center gap-2.5">
                <span className={cn("w-[34px] h-[34px] rounded-[10px] flex items-center justify-center font-mono text-[11.5px] font-semibold shrink-0", SOURCE_TONE[c.source] || "bg-surface")}>{c.source}</span>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-[16px] font-semibold leading-tight">{c.name}</div>
                  <div className="text-[11.5px] text-muted2 truncate" title={c.kind}>
                    {c.kind}
                  </div>
                </div>
                {accruedOnly ? <Pill tone="warn">Earned, not paid yet</Pill> : <Pill tone="good">Paid</Pill>}
              </div>
              <div className="flex items-baseline gap-x-2.5 gap-y-0.5 flex-wrap">
                <span className="font-display text-[24px] font-semibold leading-none">{accruedOnly ? money(c.accrued, { places: 2 }) : money(c.paid)}</span>
                <span className="text-[11.5px] text-muted2">
                  Latest: {c.latest.label} {c.latest.status === "accrued" ? "accrual report · dated" : c.latest.status === "held" ? "· held, dated" : "· paid"} {c.latest.distribution_date ? formatDate(c.latest.distribution_date) : "—"}
                </span>
              </div>
              {accruedOnly && <Threshold amount={c.accrued} threshold={c.threshold} />}
              <div className="grid grid-cols-4 rounded-lg border border-line overflow-hidden">
                {[
                  [c.source === "MLC" ? "Works paid" : c.source === "BMI" ? "Songs earning" : "Songs paid", c.works],
                  [termsFor(c.source).Plays, c.usages.toLocaleString()],
                  [c.source === "BMI" ? "Sources" : "Services", c.services],
                  [c.source === "BMI" ? "Reports" : "Statements", c.statements],
                ].map(([k, v], i) => (
                  <div key={k} className={cn("px-2 py-1.5 min-w-0", i > 0 && "border-l border-line")}>
                    <div className="text-[10.5px] text-muted2 truncate" title={k}>
                      {k}
                    </div>
                    <div className="font-mono text-[13px] truncate">{v}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => onOpenSource(c.source)} className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-brand self-start mt-auto">
                Open {c.source === "MRI" ? "Music Reports" : c.source} royalties <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </section>
          );
        })}
      </div>

      <StickyFilters className="mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="eyebrow mr-2 max-sm:hidden">Every song, across all sources</span>
          <label className="relative flex-1 min-w-[180px] max-w-[280px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Song title or artist…" aria-label="Search songs" className="w-full h-9 pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
          </label>
          <div className="flex-1" />
          <ViewToggle mode={mode} onChange={setMode} />
        </div>
      </StickyFilters>

      {!songs.length ? (
        <Empty body="No song matches that." />
      ) : mode === "grid" ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {songs.map((g) => (
            <article key={g.key} className="panel p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display text-[16px] font-semibold leading-tight">{titleCase(g.title)}</div>
                  {g.artist && <div className="text-[12.5px] text-muted2 truncate">{g.artist}</div>}
                  {g.disputed ? <DisputeTag /> : g.not_ours ? <NotOursTag /> : null}
                </div>
                <SourceMatch g={g} cols={cols} onOpen={openSong} />
              </div>
              <div className="space-y-1">
                {cols.map((c) =>
                  g.sources[c.source] ? (
                    <button key={c.source} onClick={() => openSong(g, c)} className="w-full flex items-center justify-between gap-2 py-1.5 px-2 -mx-2 rounded-lg hover:bg-surface text-left">
                      <span className="inline-flex items-center gap-2 text-[12.5px] text-muted2">
                        <SourceTag source={c.source} /> {c.has_accrued && !c.has_paid ? "accrued" : "paid"}
                      </span>
                      <Money value={amountOf(g, c)} className="text-[12.5px]" places={c.source === "BMI" ? 2 : undefined} />
                    </button>
                  ) : null
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  <th className="t-head px-4 py-2.5 font-medium text-left">Song</th>
                  <th className="t-head px-4 py-2.5 font-medium text-left">Earning at</th>
                  {cols.map((c) => (
                    <th key={c.source} className="t-head px-4 py-2.5 font-medium text-right">
                      {c.source} · {c.has_accrued && !c.has_paid ? "accrued" : "paid"}
                    </th>
                  ))}
                  <th className="t-head px-4 py-2.5 font-medium text-left">In your catalog</th>
                </tr>
              </thead>
              <tbody>
                {songs.map((g) => (
                  <tr key={g.key} className="t-row">
                    <td className="t-cell max-w-[340px]">
                      <div className="truncate" title={g.artist ? `${titleCase(g.title)} · ${g.artist}` : titleCase(g.title)}>
                        <span className="font-semibold">{titleCase(g.title)}</span>
                        {g.artist && <span className="ml-1.5 text-muted2">{g.artist}</span>}
                        {g.disputed ? <DisputeTag /> : g.not_ours ? <NotOursTag /> : null}
                      </div>
                    </td>
                    <td className="t-cell">
                      <span className="inline-flex gap-1.5">
                        {cols.map((c) => (g.sources[c.source] ? <SourceTag key={c.source} source={c.source} /> : null))}
                      </span>
                    </td>
                    {cols.map((c) => (
                      <td key={c.source} className="t-cell text-right text-[12.5px]">
                        {g.sources[c.source] ? (
                          <button onClick={() => openSong(g, c)} className="hover:underline underline-offset-2" title={`See the ${c.source} lines`}>
                            <Money value={amountOf(g, c)} places={c.source === "BMI" ? 2 : undefined} />
                          </button>
                        ) : (
                          <span className="text-muted2">—</span>
                        )}
                      </td>
                    ))}
                    <td className="t-cell">
                      <SourceMatch g={g} cols={cols} onOpen={openSong} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-line text-[12.5px] text-muted2">
            {songs.length} song{songs.length === 1 ? "" : "s"} · click an amount to see the lines behind it
          </div>
        </div>
      )}
      <WorkSheet
        code={open?.code}
        params={sheetParams}
        t={termsFor(open?.source, open?.accrued ? "accrued" : "paid")}
        open={!!open}
        onOpenChange={(v) => !v && setOpen(null)}
        canEdit={canEdit}
        onChanged={onChanged}
      />
    </Layout>
  );
}

export default function Royalties() {
  const { user } = useAuth();
  return user?.role === "writer" ? <MyRoyalties /> : <PublisherRoyalties />;
}

function PublisherRoyalties() {
  const { user } = useAuth();
  const canEdit = user?.role !== "writer";
  const [params, setParams] = useSearchParams();
  const [info, setInfo] = useRemembered("royalties:sources");
  const { tenantId } = useTenant();
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/royalties/sources");
      setInfo(data);
      warmRoyalties((data.sources || []).map((x) => x.source), tenantId);
    } catch (err) {
      toast.error(apiError(err));
    }
  }, [setInfo, tenantId]);
  useEffect(() => {
    load();
  }, [load]);

  if (!info)
    return (
      <Layout title="Royalties" subtitle="Loading…">
        <LoadingPanel rows={6} />
      </Layout>
    );
  if (!info.statements)
    return (
      <Layout title="Royalties" subtitle="What your songs earned">
        <div className="panel">
          <EmptyState
            icon={CircleDollarSign}
            title="No statements yet"
            body={canEdit ? "Drop in your MLC statement files, a BMI accrual report PDF or a Music Reports (TikTok) statement. Each is recognised and filed under its own source, and duplicates are spotted for you." : "Nothing has been imported yet."}
            action={
              canEdit && (
                <Button asChild className="bg-brand hover:bg-brand-hover">
                  <Link to="/royalties/import">
                    <Upload className="w-4 h-4 mr-2" /> Import a statement
                  </Link>
                </Button>
              )
            }
          />
        </div>
      </Layout>
    );
  const names = info.sources.map((s) => s.source);
  const asked = params.get("source");
  const view = asked && (asked === "all" || names.includes(asked)) ? asked : names.length > 1 ? "all" : names[0];
  const go = (v) => setParams(v === "all" && names.length > 1 ? {} : { source: v });
  const switcher = names.length > 1 ? <SourceSwitch value={view} sources={info.sources} onChange={go} /> : null;
  if (view === "all" && names.length > 1) return <AllSources info={info} switcher={switcher} onOpenSource={go} canEdit={canEdit} onChanged={load} />;
  return <SourceRoyalties key={view} source={view === "all" ? names[0] : view} switcher={switcher} onDataChanged={load} />;
}
